import { datadogRum } from '@datadog/browser-rum';
import { Alert, Divider, Modal, Spinner, Text } from '@ksyos/design-system';
import type { IHolterDesktopProcess } from '@frontend/components/interfaces';
import { ProcessEntity } from '@common/enums/processEntity';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import { desktopProcessErrorCodeText } from '@frontend/components/pages/episodeCategory/customControls/cardiology/holter/utils/desktopProcessErrorCodeText';
import type { IEpisodeContext } from '../../../episodes/EpisodeContext';
import type { ICustomControlProcedureAddEditProps } from '../../interfaces/customControlProcedureAddEditProps';
import { DesktopProcessActions } from '../../../../../../common/enums/desktopProcessActions';
import { DesktopProcessErrorCode } from '../../../../../../common/enums/desktopProcessErrorCodes';
import { Gender } from '../../../../../../common/enums/gender';
import { HardwareProcessStatus } from '../../../../../../common/enums/hardwareProcessStatus';
import { HolterPhase } from '../../../../../../common/enums/holterPhase';
import { RecorderType } from '../../../../../../common/enums/recorderType';
import { ToastType } from '../../../../../../common/enums/toastType';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { getHolterDiaryUrl } from '../../../../../utils/getHolterDiaryUrl';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast } from '../../../../../utils/showToast';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { Button, ButtonContainer, Form, FormDivider, HeaderDivider, InputTextArea } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormGroup } from '../../../../elements/forms/FormGroup';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { openKsyosUrl, OpenKsyosUrlResult } from '../../../../elements/openKsyosUrl';
import { HolterInstructionModal } from '../../../episodeCategory/customControls/cardiology/holter/customComponents/HolterInstructionModal';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { GraderSelector } from '../../utils/v1/GraderSelector';
import { NewOrEditProcedureProcessContainer } from '../../utils/v1/NewOrEditProcedureProcessContainer';
import { ServiceOrganizationConsumerSelector } from '../../utils/v1/ServiceOrganizationConsumerSelector';
import { HolterProcedureType, TcohFollowingSteps } from './TcohFollowingSteps';
import { TcohV2ProcedureAddEditCustomControlFields } from './TcohV2ProcedureAddEditCustomControlFields';

interface ITcohV2ProcedureAddEditState {
    buttonDisabled?: boolean;
    hasDesktopProcessTimedOut?: boolean;

    isMissingKsyosUrlHandler?: boolean;
    isProcessing?: boolean;
    processError?: string;
    processStatus?: HardwareProcessStatus;
    recorderType?: RecorderType;

    requestId?: string;
    showModal?: boolean;
}

export function TcohV2ProcedureAddEdit(props: ICustomControlProcedureAddEditProps) {
    const episodeContext = useEpisodeContext();

    return (
        <InnerTcohV2ProcedureAddEdit
            {...props}
            {...episodeContext}
        />
    );
}

const timeoutIntervalSeconds = 2;
const timeoutIntervalMilliseconds = timeoutIntervalSeconds * 1000;
const maxWaitTimeSeconds = 120;
class InnerTcohV2ProcedureAddEdit extends Component<
    ICustomControlProcedureAddEditProps & IEpisodeContext,
    ITcohV2ProcedureAddEditState
> {
    private timeout: number;
    private timeWaited = 0;

    public async componentDidMount() {
        if (
            this.props.request.isDirectProcedureAdded &&
            !this.props.procedure.customControlData.geslachtHolter &&
            (this.props.patient.gender === Gender.male || this.props.patient.gender === Gender.female)
        ) {
            await this.props.handleChangeCustomControl({ geslachtHolter: this.props.patient.gender });
        }
    }

    public render() {
        return (
            <NewOrEditProcedureProcessContainer
                currentProcedure={this.props.currentProcedure}
                isBioGrader={this.props.isBioGrader}
                isFirstItem={this.props.isFirstItem}
                process={this.props.process}
                service={this.props.service}
            >
                {this.addEditForm()}
            </NewOrEditProcedureProcessContainer>
        );
    }

    private addEditForm() {
        return (
            <Form onSubmit={async () => null}>
                <CareProviderSelector
                    {...this.props}
                    entity={this.props.procedure}
                />

                <ServiceOrganizationConsumerSelector {...this.props} />

                {this.props.request.isDirectProcedureAdded && (
                    <>
                        <FormDivider />
                        <HeaderDivider text="Holter instellen" />
                    </>
                )}

                {this.state.showModal && this.renderHolterConfiguredModal()}

                {this.isDesktopProcessRunning() &&
                    !this.state.hasDesktopProcessTimedOut &&
                    this.renderHolterConfigurationProgressModal()}

                {this.props.request.isDirectProcedureAdded && (
                    <>
                        <TcohV2ProcedureAddEditCustomControlFields
                            customControlData={this.props.procedure.customControlData}
                            {...this.props}
                        />

                        <FormDivider />

                        <HeaderDivider text={translateKey('common.remarks')} />
                        <HorizontalFormGroup hasErrors={this.props.errors.has('remark')}>
                            <FormLabel>{translateKey('common.remarks')}</FormLabel>
                            <InputContainer>
                                <InputTextArea
                                    automation="remark"
                                    error={this.props.errors.get('remark')}
                                    onChange={async (value) => await this.props.handleChange('remark', value)}
                                    onDoValidate={async () => await this.props.validateOne('remark')}
                                    placeholder={translateKey('common.remarksOrQuestions')}
                                    value={this.props.procedure.remark}
                                />
                            </InputContainer>
                        </HorizontalFormGroup>
                    </>
                )}

                <FormGroup>{this.holterStatus()}</FormGroup>

                <GraderSelector
                    {...this.props}
                    careProviderAndRequesterAndSelectedProvider={{ ...this.props.procedure }}
                    procedurePerformerOrganizationId={this.props.organizationId}
                />

                <Divider />
                <TcohFollowingSteps holterType={HolterProcedureType.Practice} />

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={this.props.onCancel}
                            entity={ProcessEntity.procedure}
                            isExisting={!!this.props.request.id}
                            revertChanges={this.props.revertProcedureChanges}
                            serviceCode={this.props.service.code}
                            serviceType={this.props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={this.state.buttonDisabled}
                            save={async () => {
                                await this.saveProcedure();
                            }}
                        />,
                        <Button
                            color="secondary"
                            disabled={this.state.buttonDisabled || !this.canConfigureHolter()}
                            large
                            onClick={async () => await this.startHolter()}
                        >
                            Stel in
                        </Button>,
                    ]}
                />
            </Form>
        );
    }

    private async saveProcedure() {
        await this.setState({ buttonDisabled: true });

        const validatedProcedure = await this.props.validateProcedure('draft');
        if (!validatedProcedure) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        const requestId = await this.props.onSaveProcedure('draft');
        await this.setState({ buttonDisabled: false });
        if (!requestId) {
            return;
        }

        showServiceToast(
            ToastType.success,
            'toast.draftSuccess',
            this.props.service,
            'procedure',
            translateKey('toast.draftDefaultMessage')
        );

        await this.props.onSave(requestId);
    }

    private isDesktopProcessRunning() {
        return this.state.processStatus === HardwareProcessStatus.starting;
    }

    private async startHolter() {
        if (this.isDesktopProcessRunning() && !this.state.hasDesktopProcessTimedOut) {
            return;
        }

        await this.setState({ buttonDisabled: true });
        const validatedProcedure = await this.props.validateProcedure('final');
        if (!validatedProcedure) {
            await this.setState({ buttonDisabled: false, isProcessing: false, processStatus: undefined });

            return;
        }

        await this.setState({
            isProcessing: true,
            hasDesktopProcessTimedOut: false,
            processStatus: HardwareProcessStatus.starting,
            processError: undefined,
        });

        this.timeWaited = 0;
        await this.props.handleChangeCustomControl({
            desktopProcessId: undefined,
            hasDesktopProcessTimedOut: false,
        });

        const requestId = await this.props.onSaveProcedure('draft');
        await this.setState({ buttonDisabled: false });
        if (!requestId) {
            return;
        }

        await this.setState({ requestId });

        let procedureId = this.props.procedure.id;
        if (!procedureId) {
            const procedure = await fetchJson(`/api/requests/${requestId}/procedure`, 'GET');
            procedureId = procedure.json.id;
        }

        const params = {
            procedurePhase: HolterPhase.configuration,
        };

        const desktopProcess = await fetchJson('/api/desktopProcesses', 'POST', {
            serviceId: this.props.service.id,
            action: DesktopProcessActions.configureHolter,
            requestId,
            params,
        });

        const desktopProcessId = desktopProcess.json.id;
        await this.props.handleChangeCustomControl({ desktopProcessId: desktopProcessId });

        const desktopParams = {
            ...params,
            employmentId: this.props.employmentId,
            desktopProcessId: desktopProcessId,
            token: desktopProcess.json.token,
            apiUrl: location.protocol + '//' + location.host,
        };

        const url =
            'ksyos://holter/fysiologic?encoded=' +
            btoa(JSON.stringify(desktopParams)).replace(/\+/g, '-').replace(/\//g, '_');
        const openResult = await openKsyosUrl(url);

        if (openResult === OpenKsyosUrlResult.noHandler) {
            await this.setState({ processStatus: undefined, isMissingKsyosUrlHandler: true, isProcessing: false });
        } else {
            await this.desktopStatusInterval(procedureId!, desktopProcessId);
        }

        if (
            this.props.procedure.draftCareProviderPractitionerId !== this.props.patient.careProviderPractitionerId ||
            this.props.procedure.draftCareProviderOrganizationId !== this.props.patient.careProviderOrganizationId
        ) {
            await fetchJson(`/api/patients/${this.props.patient.id}/careProvider`, 'POST', {
                careProviderPractitionerId: this.props.procedure.draftCareProviderPractitionerId,
                careProviderOrganizationId: this.props.procedure.draftCareProviderOrganizationId,
            });
        }
    }

    private async desktopStatusInterval(procedureId: string, desktopProcessId: string) {
        try {
            const url = `/api/desktopProcesses/${this.props.procedure.customControlData.desktopProcessId}`;
            const result = await fetchJson(url, 'GET');

            const desktopProcess: IHolterDesktopProcess = result.json;

            // CS-4179: These three lines should become this single line:
            // if (desktopProcess.status === HardwareProcessStatus.starting || desktopProcess.status === HardwareProcessStatus.running) {
            const bittiumRunning =
                desktopProcess.status === HardwareProcessStatus.running &&
                desktopProcess.data &&
                desktopProcess.data.recorderType === RecorderType.bittium;
            if (desktopProcess.status === HardwareProcessStatus.starting || bittiumRunning) {
                if (this.timeWaited >= maxWaitTimeSeconds) {
                    await this.setState({ hasDesktopProcessTimedOut: true });
                } else {
                    this.timeWaited += timeoutIntervalSeconds;
                    this.timeout = window.setTimeout(async () => {
                        await this.desktopStatusInterval(procedureId, desktopProcessId);
                    }, timeoutIntervalMilliseconds);
                }
            } else if (desktopProcess.status === HardwareProcessStatus.running) {
                // CS-4179: Move this logic to the completed else-if block.
                await this.setState({ isProcessing: false });
                await this.handleConfiguredRecorder(procedureId, RecorderType.fysiologic);
            } else if (desktopProcess.status === HardwareProcessStatus.completed) {
                await this.setState({ isProcessing: false });
                if (desktopProcess.data && desktopProcess.data.error) {
                    await this.setState({
                        processStatus: HardwareProcessStatus.failed,
                        processError: desktopProcess.data.error,
                    });
                } else if (
                    desktopProcess.data &&
                    (desktopProcess.data.recorderType === RecorderType.fysiologic ||
                        desktopProcess.data.recorderType === RecorderType.bittium)
                ) {
                    await this.handleConfiguredRecorder(procedureId, desktopProcess.data.recorderType);
                } else {
                    await this.setState({
                        processStatus: HardwareProcessStatus.failed,
                        processError: DesktopProcessErrorCode.WRITING_CONFIG_TO_HOLTER_FAILED,
                    });
                }
            } else {
                console.log(
                    `Unsupported desktop process status '${desktopProcess.status}' during holter configuration`
                );
                await this.setState({ isProcessing: false });
                await this.setState({
                    processStatus: HardwareProcessStatus.failed,
                    processError: DesktopProcessErrorCode.WRITING_CONFIG_TO_HOLTER_FAILED,
                });
            }
        } catch (e) {
            window.clearTimeout(this.timeout);
            await this.setState({ isProcessing: false });
        }
    }

    private async handleConfiguredRecorder(procedureId: string, recorderType: RecorderType) {
        datadogRum.addAction('holter configured');
        const procedure = (await fetchJson(`/api/procedures/${procedureId}/customControlData`, 'GET')).json;
        await this.setState({ showModal: true, processStatus: HardwareProcessStatus.completed, recorderType });
        await this.props.handleChangeCustomControl(procedure.customControlData);
    }

    private renderHolterConfigurationProgressModal() {
        return (
            <Modal
                closeOnEsc={false}
                isOpen
                onClose={() => undefined}
                size="small"
            >
                <Modal.Header>Een ogenblik geduld...</Modal.Header>
                <Modal.Body>
                    {this.state.processStatus === HardwareProcessStatus.starting && (
                        <Text>
                            Het instellen van de recorder is gestart{' '}
                            <Spinner style={{ marginLeft: 'var(--kds-spacing-1)' }} />
                        </Text>
                    )}
                </Modal.Body>
                <Modal.Footer />
            </Modal>
        );
    }

    private renderHolterConfiguredModal() {
        const requestId = this.state.requestId || this.props.request.id;
        if (!requestId) {
            throw new Error('request id undefined');
        }

        if (!this.state.recorderType) {
            throw new Error('Missing recorder type');
        }

        return (
            <HolterInstructionModal
                holterDiaryUrl={getHolterDiaryUrl(requestId, this.props.patient)}
                onClose={async () => {
                    await this.setState({ showModal: false });
                    await this.props.onSave(requestId);
                }}
                recorderType={this.state.recorderType}
            />
        );
    }

    private holterStatus() {
        if (this.state.isMissingKsyosUrlHandler) {
            return (
                <Alert appearance="warning">
                    De nieuwe Ksyos koppeling is nog niet geïnstalleerd,{' '}
                    <a
                        href={`/desktop?organizationId=${this.props.organizationId}`}
                        rel="noreferrer"
                        target="_blank"
                    >
                        download hem hier
                    </a>
                    .
                </Alert>
            );
        } else if (this.state.processStatus === HardwareProcessStatus.failed) {
            const recorderFailureMessage =
                'Er is wat mis met deze recorder, neem contact op met de Servicedesk dan zoeken we het voor je uit.';

            if (this.state.processError) {
                if (this.state.processError === DesktopProcessErrorCode.HOLTER_NOT_SET_BUT_CONTAINS_RECORDING) {
                    return (
                        <Alert appearance="warning">
                            Deze recorder bevat een opname. Mogelijk is de recorder per ongeluk gestart. Je kan de
                            recorder{' '}
                            <Alert.Link
                                to="/holter/dashboard"
                                variant="link"
                            >
                                op het recorderoverzicht
                            </Alert.Link>{' '}
                            leeg maken, zodat deze weer in te stellen is.
                        </Alert>
                    );
                }

                const errorText = desktopProcessErrorCodeText[this.state.processError];

                return <Alert appearance="warning">{errorText ? errorText : recorderFailureMessage}</Alert>;
            } else {
                return <Alert appearance="warning">{recorderFailureMessage}</Alert>;
            }
        } else if (this.state.hasDesktopProcessTimedOut) {
            return (
                <Alert appearance="warning">
                    Wij denken dat je de Ksyos koppeling nog niet hebt geïnstalleerd op deze computer.{' '}
                    <a
                        href={`/desktop?organizationId=${this.props.organizationId}`}
                        rel="noreferrer"
                        target="_blank"
                    >
                        Download deze hier
                    </a>{' '}
                    of neem contact op met onze Servicedesk.
                </Alert>
            );
        }

        if (!this.props.request.isDirectProcedureAdded) {
            return <Alert appearance="info">Sluit nu de recorder aan op de computer om deze in te stellen</Alert>;
        }
    }

    private canConfigureHolter() {
        if (!this.props.request.isDirectProcedureAdded) {
            return true;
        }
        const careProviderIsChosen =
            !this.props.procedure.careProviderIsNotRequester &&
            ((this.props.patient.careProviderPractitionerId && this.props.patient.careProviderOrganizationId) ||
                (this.props.procedure.draftCareProviderPractitionerId &&
                    this.props.procedure.draftCareProviderOrganizationId)) &&
            this.props.serviceProvider?.canSelectCareProviderAsServiceConsumer;

        return (
            this.props.procedure.customControlData.procedureType &&
            (this.props.procedure.customControlData.eventEcgCardioExaminationDuration ||
                this.props.procedure.customControlData.holterCardioExaminationDuration) &&
            (this.props.procedure.requesterPractitionerId ||
                this.props.procedure.requesterOrganizationId ||
                careProviderIsChosen) &&
            this.props.procedure.customControlData.geslachtHolter &&
            this.props.procedure.customControlData.hasPacemaker
        );
    }
}
